'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'

// =============================================================================
// DATA
// =============================================================================

const artists = [
  {
    id: 'yayoi-kusama',
    name: 'Yayoi Kusama',
    initials: 'YK',
    color: '#E85D75',
    nationality: 'Japanese',
    country: 'JP',
    born: 1929,
    tier: 'Blue-chip',
    careerStage: 'Established',
    movements: ['Pop Art', 'Minimalism'],
    marketCap: '$2.4B',
    trend: 18.4,
    avgHammer: '$1.8M',
    lotsLast12m: 342,
  },
  {
    id: 'jean-michel-basquiat',
    name: 'Jean-Michel Basquiat',
    initials: 'JB',
    color: '#E63946',
    nationality: 'American',
    country: 'US',
    born: 1960,
    died: 1988,
    tier: 'Blue-chip',
    careerStage: 'Historical',
    movements: ['Neo-Expressionism', 'Street Art'],
    marketCap: '$4.1B',
    trend: 12.1,
    avgHammer: '$3.2M',
    lotsLast12m: 186,
  },
  {
    id: 'gerhard-richter',
    name: 'Gerhard Richter',
    initials: 'GR',
    color: '#7B68EE',
    nationality: 'German',
    country: 'DE',
    born: 1932,
    tier: 'Blue-chip',
    careerStage: 'Established',
    movements: ['Conceptual Art', 'Photo-Realism'],
    marketCap: '$3.2B',
    trend: -2.3,
    avgHammer: '$2.1M',
    lotsLast12m: 245,
  },
  {
    id: 'yoshitomo-nara',
    name: 'Yoshitomo Nara',
    initials: 'YN',
    color: '#FF69B4',
    nationality: 'Japanese',
    country: 'JP',
    born: 1959,
    tier: 'Blue-chip',
    careerStage: 'Established',
    movements: ['Pop Art', 'Neo-Pop'],
    marketCap: '$890M',
    trend: 24.7,
    avgHammer: '$1.2M',
    lotsLast12m: 198,
  },
  {
    id: 'banksy',
    name: 'Banksy',
    initials: 'B',
    color: '#2A2A2A',
    nationality: 'British',
    country: 'UK',
    born: 1974,
    tier: 'Blue-chip',
    careerStage: 'Mid-career',
    movements: ['Street Art'],
    marketCap: '$1.2B',
    trend: 8.5,
    avgHammer: '$580K',
    lotsLast12m: 1240,
  },
  {
    id: 'amoako-boafo',
    name: 'Amoako Boafo',
    initials: 'AB',
    color: '#4ECDC4',
    nationality: 'Ghanaian',
    country: 'GH',
    born: 1984,
    tier: 'Mid-market',
    careerStage: 'Emerging',
    movements: ['African Contemporary'],
    marketCap: '$45M',
    trend: 42.1,
    avgHammer: '$420K',
    lotsLast12m: 48,
  },
  {
    id: 'david-hockney',
    name: 'David Hockney',
    initials: 'DH',
    color: '#4A90D9',
    nationality: 'British',
    country: 'UK',
    born: 1937,
    tier: 'Blue-chip',
    careerStage: 'Established',
    movements: ['Pop Art', 'Photorealism'],
    marketCap: '$1.8B',
    trend: 6.2,
    avgHammer: '$2.4M',
    lotsLast12m: 312,
  },
  {
    id: 'claude-monet',
    name: 'Claude Monet',
    initials: 'CM',
    color: '#9B7EBD',
    nationality: 'French',
    country: 'FR',
    born: 1840,
    died: 1926,
    tier: 'Blue-chip',
    careerStage: 'Historical',
    movements: ['Impressionism'],
    marketCap: '$8.2B',
    trend: 4.8,
    avgHammer: '$12.5M',
    lotsLast12m: 42,
  },
]

const movements = ['All', 'Pop Art', 'Street Art', 'Neo-Expressionism', 'Impressionism', 'Minimalism', 'African Contemporary']
const tiers = ['All Tiers', 'Blue-chip', 'Mid-market', 'Emerging']

// Trending artists with colors
const trendingArtists = [
  { name: 'Amoako Boafo', initials: 'AB', color: '#4ECDC4', trend: 42.1, reason: 'Museum acquisitions' },
  { name: 'Yoshitomo Nara', initials: 'YN', color: '#FF69B4', trend: 24.7, reason: 'Major retrospective' },
  { name: 'Yayoi Kusama', initials: 'YK', color: '#E85D75', trend: 18.4, reason: 'Asia demand surge' },
  { name: 'Jadé Fadojutimi', initials: 'JF', color: '#6B5B95', trend: 156.2, reason: 'Secondary debut' },
  { name: 'Christina Quarles', initials: 'CQ', color: '#F7CAC9', trend: 89.4, reason: 'Venice Biennale' },
]

// Career stage data
const careerStageData = [
  { stage: 'Emerging', count: '45K', avgPrice: '$28K', growth: 32.4 },
  { stage: 'Mid-career', count: '62K', avgPrice: '$185K', growth: 12.8 },
  { stage: 'Established', count: '48K', avgPrice: '$1.2M', growth: 6.2 },
  { stage: 'Historical', count: '25K', avgPrice: '$4.8M', growth: 3.1 },
]

// Recent records
const recentRecords = [
  { artist: 'Claude Monet', initials: 'CM', color: '#9B7EBD', work: 'Nymphéas', price: '$74.0M', house: "Christie's", prevRecord: '$62M' },
  { artist: 'J-M Basquiat', initials: 'JB', color: '#E63946', work: 'Untitled (Devil)', price: '$42.4M', house: "Christie's", prevRecord: '$38M' },
  { artist: 'Jadé Fadojutimi', initials: 'JF', color: '#6B5B95', work: 'Yet, To Be Titled', price: '$1.5M', house: 'Phillips', prevRecord: '$680K' },
]

// =============================================================================
// COMPONENTS
// =============================================================================

function Sparkline({ positive }: { positive: boolean }) {
  const data = positive ? [20, 22, 21, 25, 28, 32, 35, 38, 42] : [40, 38, 35, 37, 32, 28, 25, 22, 20]
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const width = 48
  const height = 20

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={positive ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ArtistsPage() {
  const [search, setSearch] = useState('')
  const [selectedMovement, setSelectedMovement] = useState('All')
  const [selectedTier, setSelectedTier] = useState('All Tiers')
  const [sortBy, setSortBy] = useState<'marketCap' | 'trend' | 'avgHammer'>('marketCap')
  const { user } = useAuth()
  const isFreeUser = user?.tier === 'free' || !user

  const filteredArtists = useMemo(() => {
    return artists.filter((artist) => {
      const matchesSearch = artist.name.toLowerCase().includes(search.toLowerCase())
      const matchesMovement = selectedMovement === 'All' || artist.movements.includes(selectedMovement)
      const matchesTier = selectedTier === 'All Tiers' || artist.tier === selectedTier
      return matchesSearch && matchesMovement && matchesTier
    }).sort((a, b) => {
      switch (sortBy) {
        case 'trend': return b.trend - a.trend
        case 'avgHammer': return parseFloat(b.avgHammer.replace(/[$KMB]/g, '')) - parseFloat(a.avgHammer.replace(/[$KMB]/g, ''))
        default: return parseFloat(b.marketCap.replace(/[$KMB]/g, '')) - parseFloat(a.marketCap.replace(/[$KMB]/g, ''))
      }
    })
  }, [search, selectedMovement, selectedTier, sortBy])

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl md:text-3xl font-light">Artists</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-accent-primary/10 text-accent-primary">180K+</span>
          </div>
          <p className="text-text-secondary text-sm">Market analytics on fine artists worldwide</p>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'marketCap' | 'trend' | 'avgHammer')}
          className="px-3 py-2 rounded-lg text-sm bg-card border border-border text-text-secondary"
        >
          <option value="marketCap">Sort by Market Cap</option>
          <option value="trend">Sort by Trend</option>
          <option value="avgHammer">Sort by Avg. Hammer</option>
        </select>
      </div>

      {/* AI Signal - Condensed */}
      <div className="card p-4 mb-6 flex items-center gap-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span className="font-medium">AI Signal:</span>{' '}
            <span className="text-text-secondary">African Contemporary +42% YoY. Emerging female artists outperforming by 3.2x.</span>
          </p>
        </div>
        {isFreeUser && (
          <Link href="/get-started" className="text-xs text-accent-primary hover:underline flex-shrink-0">
            View insights →
          </Link>
        )}
      </div>

      {/* Search & Filters */}
      <div className="mb-6">
        <div className="relative max-w-md mb-4">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search artists..."
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-border bg-card text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-border-hover text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {movements.map((movement) => (
            <button
              key={movement}
              onClick={() => setSelectedMovement(movement)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                selectedMovement === movement
                  ? 'bg-accent-primary text-white'
                  : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {movement}
            </button>
          ))}
          <div className="w-px h-5 bg-border mx-1" />
          <select
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
            className={`px-3 py-1.5 rounded-full text-sm border bg-transparent cursor-pointer ${
              selectedTier !== 'All Tiers' ? 'border-accent-primary text-accent-primary' : 'border-border text-text-secondary'
            }`}
          >
            {tiers.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
          </select>
        </div>
      </div>

      {/* Three Column: Trending + Career Stage + Recent Records */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Trending */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium text-sm">Trending</h2>
            <span className="text-xs text-text-tertiary">30d</span>
          </div>
          <div className="space-y-2">
            {trendingArtists.slice(0, 4).map((artist, i) => (
              <div key={artist.name} className="flex items-center gap-2 py-1.5">
                <span className="text-xs text-text-tertiary w-3">{i + 1}</span>
                <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium flex-shrink-0" style={{ background: artist.color }}>
                  {artist.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{artist.name}</p>
                  <p className="text-xs text-text-tertiary">{artist.reason}</p>
                </div>
                <span className="text-xs font-medium text-positive tabular-nums">+{artist.trend}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Career Stage */}
        <div className="card p-4">
          <h2 className="font-medium text-sm mb-3">By Career Stage</h2>
          <div className="space-y-2">
            {careerStageData.map((stage) => (
              <Link
                key={stage.stage}
                href={`/app/market/career/${stage.stage.toLowerCase()}`}
                className="flex items-center justify-between py-1.5 hover:bg-page -mx-2 px-2 rounded-lg transition-colors"
              >
                <div>
                  <p className="text-sm font-medium">{stage.stage}</p>
                  <p className="text-xs text-text-tertiary">{stage.count} · Avg {stage.avgPrice}</p>
                </div>
                <span className="text-xs font-medium text-positive tabular-nums">+{stage.growth}%</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Records */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-medium text-sm">Recent Records</h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">All →</Link>
          </div>
          <div className="space-y-2">
            {recentRecords.map((record, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5">
                <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium flex-shrink-0" style={{ background: record.color }}>
                  {record.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{record.artist}</p>
                  <p className="text-xs text-text-tertiary truncate">{record.work}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-positive tabular-nums">{record.price}</p>
                  <p className="text-xs text-text-tertiary">was {record.prevRecord}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-text-tertiary mb-4">
        {filteredArtists.length} artists
      </p>

      {/* Artist grid - Condensed cards with color initials */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        {filteredArtists.map((artist, i) => (
          <motion.div
            key={artist.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03, duration: 0.2 }}
          >
            <Link
              href={`/app/artists/${artist.id}`}
              className="group card p-4 block hover:shadow-lg hover:border-border-hover transition-all"
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                  style={{ background: artist.color }}
                >
                  {artist.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm group-hover:text-accent-primary transition-colors truncate">
                    {artist.name}
                  </h3>
                  <p className="text-text-tertiary text-xs">
                    {artist.nationality}, {artist.born}{artist.died ? `–${artist.died}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  artist.tier === 'Blue-chip' ? 'bg-accent-primary/10 text-accent-primary'
                    : artist.tier === 'Mid-market' ? 'bg-accent-warm/10 text-accent-warm'
                    : 'bg-positive/10 text-positive'
                }`}>
                  {artist.tier}
                </span>
                <Link
                  href={`/app/market/career/${artist.careerStage.toLowerCase().replace('-', '')}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs px-1.5 py-0.5 rounded border border-border text-text-tertiary hover:border-accent-primary hover:text-accent-primary transition-colors"
                >
                  {artist.careerStage}
                </Link>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
                <div>
                  <p className="font-display text-lg font-light">{artist.marketCap}</p>
                  <p className="text-xs text-text-tertiary">Market cap</p>
                </div>
                <div className="flex items-center gap-3">
                  <Sparkline positive={artist.trend >= 0} />
                  <span
                    className="text-sm font-medium tabular-nums"
                    style={{ color: artist.trend >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                  >
                    {artist.trend >= 0 ? '+' : ''}{artist.trend}%
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Empty state */}
      {filteredArtists.length === 0 && (
        <div className="text-center py-12">
          <p className="text-text-tertiary mb-4">No artists found</p>
          <button
            onClick={() => { setSearch(''); setSelectedMovement('All'); setSelectedTier('All Tiers') }}
            className="text-accent-primary hover:underline text-sm"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Upgrade CTA */}
      {isFreeUser && (
        <section className="card p-5 bg-gradient-to-br from-accent-primary/5 to-transparent border-accent-primary/20">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1">
              <h3 className="font-medium mb-1">Access artist intelligence</h3>
              <p className="text-sm text-text-secondary">AI recommendations, full history, and collector insights.</p>
            </div>
            <Link href="/get-started" className="px-5 py-2 rounded-xl font-medium text-sm text-white flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>
              From $199/mo
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}
